// i18next runtime for the web UI. Mirrors the iOS localization model: every
// user-facing string IS its own key (the English source text), so this catalog
// shares its convention — and even its filled translations — with the iOS
// `Localizable.xcstrings`. See `backend/app/scripts/translate_web_strings.py`,
// which fills `locales/*.json` from English, reusing the iOS catalog verbatim.
//
// Language resolution: an authenticated user's `profile.preferred_language` is
// applied on top at login (see `auth/auth-context.tsx`); before that the
// detector resolves localStorage → browser locale → English.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { UI_LANGUAGE_CODES, isRtl } from "./languages";

import en from "./locales/en.json";
import de from "./locales/de.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";
import it from "./locales/it.json";
import pt from "./locales/pt.json";
import nl from "./locales/nl.json";
import ja from "./locales/ja.json";
import zh from "./locales/zh.json";
import ko from "./locales/ko.json";
import ar from "./locales/ar.json";
import ru from "./locales/ru.json";
import hi from "./locales/hi.json";
import pl from "./locales/pl.json";
import tr from "./locales/tr.json";

/** localStorage key the detector caches the chosen UI language under. */
export const UI_LANG_STORAGE_KEY = "ep.uiLang";

const resources = {
  en: { translation: en },
  de: { translation: de },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  pt: { translation: pt },
  nl: { translation: nl },
  ja: { translation: ja },
  zh: { translation: zh },
  ko: { translation: ko },
  ar: { translation: ar },
  ru: { translation: ru },
  hi: { translation: hi },
  pl: { translation: pl },
  tr: { translation: tr },
} as const;

/** Keep <html dir> + lang in sync so RTL languages (Arabic) flip the layout. */
function applyDocumentDirection(code: string): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("lang", code);
  root.setAttribute("dir", isRtl(code) ? "rtl" : "ltr");
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: UI_LANGUAGE_CODES,
    // Natural-language keys: the English source string is the key, and it can
    // contain "." / ":" — so disable the namespace + key separators.
    keySeparator: false,
    nsSeparator: false,
    load: "languageOnly",
    interpolation: { escapeValue: false }, // React already escapes.
    // Resources are bundled inline, so init is synchronous — no Suspense needed.
    react: { useSuspense: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: UI_LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnEmptyString: false, // empty unit ⇒ fall back to the English key.
  });

applyDocumentDirection(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", applyDocumentDirection);

export default i18n;
